import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search } from 'lucide-react';
import { Folder, Message } from 'iconoir-react';

import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { AddFolderModal } from './components/add-folder-modal';
import { AddPromptModal } from './components/add-prompt-modal';
import { FolderRow } from './components/folder-row';
import { PromptRow } from './components/prompt-row';
import { getErrorMessage, useOwnerDirectory, usePromptLibrary, usePromptSearch } from './hooks/use-prompts';
import type { PromptFolder, PromptItem } from './types';
import { sortFolders } from './utils';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import ConfigurationHeader from 'components/configuration/ConfigurationHeader';
import ConfigurationButton from 'components/configuration/ConfigurationButton';
import { configureCardGridClassName } from 'constant/configure-layout';

const addFolderButtonClassName =
  ' shrink-0 rounded-[100px] border border-indigo-100 bg-gradient-to-br from-white to-indigo-25 px-4 py-3 text-indigo-950 shadow-none hover:opacity-90 dark:border-border dark:from-card dark:to-card dark:text-foreground dark:hover:opacity-100 sm:w-auto';

function FolderListSkeleton() {
  return (
    <div className={configureCardGridClassName}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Card
          key={index}
          className="h-[240px] min-h-[240px] overflow-hidden rounded-2xl border border-grey-100 bg-white shadow-none dark:border-border dark:bg-card"
        >
          <div className="flex h-full flex-col gap-3 p-5">
            <div className="flex min-w-0 items-center justify-between gap-2">
              <div className="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-muted" />
              <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-muted" />
            </div>
            <div className="h-4 w-32 animate-pulse rounded bg-muted" />
            <div className="mt-auto flex items-center justify-end gap-2 pt-4">
              <div className="h-5 w-24 animate-pulse rounded-md bg-muted" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function PromptListSkeleton() {
  return (
    <div className={configureCardGridClassName}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Card
          key={index}
          className="h-[240px] min-h-[240px] overflow-hidden rounded-2xl border border-grey-100 bg-white shadow-none dark:border-border dark:bg-card"
        >
          <div className="flex h-full flex-col gap-3 p-5">
            <div className="flex min-w-0 items-center justify-between gap-2">
              <div className="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-muted" />
              <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-muted" />
            </div>
            <div className="h-4 w-40 animate-pulse rounded bg-muted" />
            <div className="h-3 w-full animate-pulse rounded bg-muted" />
            <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
            <div className="mt-auto flex items-center justify-between gap-2 pt-4">
              <div className="h-6 w-28 shrink-0 animate-pulse rounded-full bg-muted" />
              <div className="h-5 w-24 animate-pulse rounded-md bg-muted" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

export default function PromptsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<PromptFolder | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<PromptItem | null>(null);
  const [isAddPromptOpen, setIsAddPromptOpen] = useState(false);

  const { data, isLoading, isFetching, isError, error, isPending, refetch } = usePromptLibrary();

  const isSearching = search.trim().length > 0;

  const {
    data: searchPrompts = [],
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
    isError: isSearchError,
    error: searchError,
    refetch: refetchSearch,
  } = usePromptSearch(search);

  const isLoadingFolders = isLoading || isFetching || isPending;
  const isLoadingSearch = isSearchLoading || isSearchFetching;

  const folders = useMemo(() => sortFolders(data?.folders || []), [data?.folders]);

  const ownerIds = useMemo(
    () => [...folders.map((folder) => folder.ownerId), ...searchPrompts.map((prompt) => prompt.createdBy)],
    [folders, searchPrompts]
  );
  const { data: ownerDirectory = {} } = useOwnerDirectory(ownerIds);

  const defaultFolder = folders.find((folder) => folder.isDefault) || folders[0] || null;

  const folderById = useMemo(
    () => new Map(folders.map((folder) => [folder.id, folder])),
    [folders]
  );

  const folderCountLabel =
    folders.length === 1 ? t('FOLDER') || 'folder' : t('FOLDERS') || 'folders';
  const promptCountLabel =
    searchPrompts.length === 1 ? t('PROMPT') || 'prompt' : t('PROMPTS') || 'prompts';

  const openAddPrompt = (folder?: PromptFolder | null) => {
    setEditingPrompt(null);
    setSelectedFolder(folder || defaultFolder);
    setIsAddPromptOpen(true);
  };

  const openEditPrompt = (prompt: PromptItem) => {
    setEditingPrompt(prompt);
    setSelectedFolder(folderById.get(prompt.folderId) || defaultFolder);
    setIsAddPromptOpen(true);
  };

  return (
    <ConfigurationLayout>
      <div className="flex w-full min-w-0 flex-col gap-6 pb-6">
        <div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <ConfigurationHeader
            title={t('PROMPT_LIBRARY_TITLE') || 'Prompt library'}
            subtitle={
              t('PROMPT_LIBRARY_SUBTITLE') ||
              'Organize reusable prompts into folders and quickly find the instructions you need.'
            }
            icon={
              <Message
                width={20}
                height={20}
                strokeWidth={1.75}
                className="text-grey-900 dark:text-foreground"
              />
            }
          />

          <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">
            <AddFolderModal triggerClassName={addFolderButtonClassName} />
            <ConfigurationButton
              icon={<Plus className="h-4 w-4" />}
              onClick={() => openAddPrompt()}
              disabled={!defaultFolder}
            >
              {t('ADD_PROMPT') || 'Add prompt'}
            </ConfigurationButton>
          </div>
        </div>

        <div className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-grey-100 bg-white p-3 sm:max-w-[460px] dark:border-border dark:bg-card">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center p-[3px]">
            <Search className="h-[15px] w-[15px] text-grey-400" />
          </div>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('SEARCH_PROMPTS_PLACEHOLDER') || 'Search prompts...'}
            className="h-full min-h-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal leading-[1.2] shadow-none placeholder:text-grey-400 focus-visible:ring-0 dark:bg-transparent"
          />
        </div>
      </div>

      {!isSearching ? (
        <div className="flex min-w-0 flex-col gap-3">
          {/* <div className="flex min-w-0 items-center justify-between gap-3 sm:gap-4">
            <div className="flex min-w-0 items-center gap-2">
              <Folder
                width={20}
                height={20}
                strokeWidth={1.75}
                className="text-grey-900 dark:text-foreground"
              />
              <h2 className="min-w-0 text-base font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                {t('ALL_FOLDERS_TITLE') || 'All folders'}
              </h2>
            </div>
            <span className="inline-flex shrink-0 items-center rounded-lg bg-grey-50 p-3 text-sm font-normal leading-[1.2] text-grey-900 dark:bg-white/10 dark:text-foreground">
              {folders.length} {folderCountLabel}
            </span>
          </div> */}

          {isLoadingFolders ? (
            <FolderListSkeleton />
          ) : isError ? (
            <Card className="rounded-lg border border-dashed border-destructive/40 bg-white shadow-none dark:bg-card">
              <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
                <p className="text-base font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                  {t('COULD_NOT_LOAD_PROMPT_FOLDERS') || 'Could not load prompt folders'}
                </p>
                <p className="text-sm text-muted-foreground">{getErrorMessage(error)}</p>
                <Button className="mt-2" variant="outline" onClick={() => refetch()}>
                  {t('RETRY') || 'Retry'}
                </Button>
              </div>
            </Card>
          ) : folders.length === 0 ? (
            <Card className="flex h-[315px] w-full flex-col items-center justify-center gap-6 rounded-lg border border-grey-100 bg-white p-6 shadow-none dark:border-border dark:bg-card">
              <div className="flex flex-col items-center gap-1.5 text-center">
                <Folder width={20} height={20} strokeWidth={1.75} className="text-grey-500 dark:text-muted-foreground" />
                <p className="text-base font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                  {t('NO_FOLDERS_FOUND') || 'No folders found'}
                </p>
                <p className="max-w-lg text-sm font-normal leading-[1.6] text-grey-500 dark:text-muted-foreground">
                  {t('NO_FOLDERS_HELP_TEXT') ||
                    'Create your first folder to start organizing prompts for your workspace.'}
                </p>
              </div>
              <AddFolderModal
                triggerClassName="h-10 shrink-0 rounded-[62px] border border-indigo-100 bg-gradient-to-br from-white to-indigo-25 px-6 text-base font-medium leading-[1.15] text-indigo-950 shadow-none hover:opacity-90 dark:border-border dark:from-card dark:to-card dark:text-foreground dark:hover:opacity-100"
                triggerLabel={t('CREATE_FOLDER') || 'Create folder'}
              />
            </Card>
          ) : (
            <div className={configureCardGridClassName}>
              {folders.map((folder) => (
                <FolderRow key={folder.id} folder={folder} owner={ownerDirectory[folder.ownerId]} onAddPrompt={openAddPrompt} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex min-w-0 items-center justify-between gap-3 sm:gap-4">
            <div className="flex min-w-0 items-center gap-2">
              <Message
                width={20}
                height={20}
                strokeWidth={1.75}
                className="text-grey-900 dark:text-foreground"
              />
              <h2 className="min-w-0 text-base font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                {t('SEARCH_RESULTS') || 'Search results'}
              </h2>
            </div>
            {!isLoadingSearch && !isSearchError ? (
              <span className="inline-flex shrink-0 items-center rounded-lg bg-grey-50 p-3 text-sm font-normal leading-[1.2] text-grey-900 dark:bg-white/10 dark:text-foreground">
                {searchPrompts.length} {promptCountLabel}
              </span>
            ) : null}
          </div>

          {isLoadingSearch ? (
            <PromptListSkeleton />
          ) : isSearchError ? (
            <Card className="rounded-lg border border-dashed border-destructive/40 bg-white shadow-none dark:bg-card">
              <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
                <p className="text-base font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                  {t('COULD_NOT_SEARCH_PROMPTS') || 'Could not search prompts'}
                </p>
                <p className="text-sm text-muted-foreground">{getErrorMessage(searchError)}</p>
                <Button className="mt-2" variant="outline" onClick={() => refetchSearch()}>
                  {t('RETRY') || 'Retry'}
                </Button>
              </div>
            </Card>
          ) : searchPrompts.length === 0 ? (
            <Card className="rounded-lg border border-dashed border-grey-100 bg-white shadow-none dark:border-border dark:bg-card">
              <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                {t('NO_PROMPTS_FOUND') || 'No prompts found'}
                {t('NO_PROMPTS_HELP_TEXT') ? ` — ${t('NO_PROMPTS_HELP_TEXT')}` : ''}
              </div>
            </Card>
          ) : (
            <div className={configureCardGridClassName}>
              {searchPrompts.map((prompt) => (
                <PromptRow key={prompt.id} prompt={prompt} owner={prompt.createdBy ? ownerDirectory[prompt.createdBy] : undefined} showOwner onEdit={openEditPrompt} />
              ))}
            </div>
          )}
        </div>
      )}

      <AddPromptModal
        open={isAddPromptOpen}
        onOpenChange={setIsAddPromptOpen}
        folder={selectedFolder}
        prompt={editingPrompt}
      />
    </ConfigurationLayout>
  );
}
